import { useEffect, useState } from "react";
import api, { formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Crown, Check, Sparkles, Tag, Gift, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

function loadRazorpay() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

export default function Premium() {
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const [plans, setPlans] = useState([]);
  const [loadingPlans, setLoadingPlans] = useState(true);

  // Referral Coupon States
  const [myCoupons, setMyCoupons] = useState([]);
  const [couponCode, setCouponCode] = useState("");
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [validatingCoupon, setValidatingCoupon] = useState(false);

  useEffect(() => {
    loadRazorpay();
    api.get("/packages/public")
      .then(({ data }) => {
        setPlans(data);
      })
      .catch(() => {
        toast.error("Failed to load subscription plans.");
      })
      .finally(() => {
        setLoadingPlans(false);
      });

    // Fetch user's active earned referral coupons
    api.get("/coupons/my-coupons")
      .then(({ data }) => {
        if (data.active_coupons) {
          setMyCoupons(data.active_coupons);
        }
      })
      .catch(() => {});
  }, []);

  const handleApplyCoupon = async (codeToValidate) => {
    const c = (codeToValidate || couponCode).trim().toUpperCase();
    if (!c) return toast.error("Please enter a valid coupon code");
    setValidatingCoupon(true);
    try {
      const { data } = await api.post("/coupons/validate", { code: c });
      setAppliedCoupon({ code: c, discount_amount: data.discount_amount, message: data.message });
      toast.success(data.message || "₹200 Referral Discount Applied!");
    } catch (err) {
      toast.error(formatError(err));
      setAppliedCoupon(null);
    } finally {
      setValidatingCoupon(false);
    }
  };

  const pay = async (planId, planPrice) => {
    const activeCouponCode = appliedCoupon ? appliedCoupon.code : null;
    const finalPrice = appliedCoupon ? Math.max(0, planPrice - appliedCoupon.discount_amount) : planPrice;

    // Free plan or 100% coupon discount — activate directly
    if (finalPrice === 0) {
      try {
        const { data: result } = await api.post("/payments/order", { plan: planId, coupon_code: activeCouponCode });
        if (result.free_plan) {
          await refresh();
          toast.success(result.message || "✅ Plan activated successfully!");
          nav("/app");
        }
      } catch (e) {
        toast.error(formatError(e));
      }
      return;
    }

    // Paid plan — open Razorpay
    const ok = await loadRazorpay();
    if (!ok) return toast.error("Could not load Razorpay. Check your network.");
    try {
      const { data: order } = await api.post("/payments/order", { plan: planId, coupon_code: activeCouponCode });
      
      if (order.free_plan) {
        await refresh();
        toast.success(order.message || "✅ Plan activated with coupon!");
        nav("/app");
        return;
      }

      const rzp = new window.Razorpay({
        key: order.key_id,
        amount: order.amount,
        currency: order.currency,
        order_id: order.order_id,
        name: "Hamarsathi",
        description: `Premium Upgrade Package`,
        image: "https://images.unsplash.com/photo-1739994885725-0a772884d66c?w=200",
        prefill: { name: order.name || user?.name, email: order.email || user?.email },
        theme: { color: "#6B1A24" },
        handler: async (resp) => {
          try {
            await api.post("/payments/verify", {
              plan: planId,
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
              coupon_code: activeCouponCode,
            });
            await refresh();
            toast.success("Welcome to Premium! 🎉");
            nav("/app");
          } catch (e) { toast.error(formatError(e)); }
        },
        modal: { ondismiss: () => toast.info("Payment cancelled") },
      });
      rzp.on("payment.failed", (resp) => toast.error(resp?.error?.description || "Payment failed"));
      rzp.open();
    } catch (e) { toast.error(formatError(e)); }
  };

  return (
    <div className="container-px py-10">
      <div className="text-center max-w-2xl mx-auto">
        <span className="label-cap inline-flex items-center gap-1"><Crown className="w-3 h-3"/>Premium</span>
        <h1 className="font-heading text-5xl text-primary mt-3">Find faster. Connect deeper.</h1>
        <p className="text-muted-foreground mt-3">Premium unlocks unlimited messaging, see who viewed your profile, and priority visibility across all 24 Jharkhand districts.</p>
        {user?.is_premium && (
          <div className="mt-4 flex flex-col items-center gap-2">
            <div className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-secondary/15 text-secondary text-xs">
              <Crown className="w-3 h-3"/>You are a Premium member
            </div>
            <Button 
              variant="outline" 
              size="sm" 
              className="rounded-full text-xs text-red-600 hover:text-red-700 hover:bg-red-50"
              onClick={async () => {
                if (window.confirm("Are you sure you want to cancel your Premium membership?")) {
                  try {
                    await api.post("/payments/cancel");
                    await refresh();
                    toast.success("Subscription cancelled successfully.");
                  } catch (e) {
                    toast.error("Failed to cancel subscription.");
                  }
                }
              }}
              data-testid="cancel-premium-btn"
            >
              Cancel Premium Membership
            </Button>
          </div>
        )}
      </div>

      {/* Referral Coupon Redemption Card */}
      <div className="max-w-md mx-auto mt-8 p-5 bg-white rounded-2xl border border-border/80 shadow-sm space-y-3">
        <div className="flex items-center justify-between">
          <span className="font-bold text-sm text-primary flex items-center gap-1.5">
            <Tag className="w-4 h-4 text-emerald-600" /> Have a Referral Coupon Code?
          </span>
          {appliedCoupon && (
            <span className="text-xs font-semibold text-emerald-700 bg-emerald-100 px-2.5 py-0.5 rounded-full flex items-center gap-1">
              <CheckCircle2 className="w-3 h-3" /> ₹{appliedCoupon.discount_amount} OFF Applied
            </span>
          )}
        </div>

        {myCoupons.length > 0 && !appliedCoupon && (
          <div className="space-y-1.5 pt-1">
            <span className="text-xs text-muted-foreground font-medium flex items-center gap-1">
              <Gift className="w-3.5 h-3.5 text-amber-600" /> Your Earned Referral Coupons:
            </span>
            <div className="flex flex-wrap gap-2">
              {myCoupons.map((c) => (
                <button
                  key={c.code}
                  type="button"
                  onClick={() => {
                    setCouponCode(c.code);
                    handleApplyCoupon(c.code);
                  }}
                  className="text-xs px-3 py-1.5 rounded-xl bg-amber-500/10 hover:bg-amber-500/20 text-amber-900 font-mono font-bold border border-amber-500/30 flex items-center gap-1.5 transition-all"
                  data-testid={`coupon-chip-${c.code}`}
                >
                  🎟️ {c.code} (₹200 OFF)
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="flex gap-2">
          <Input
            placeholder="e.g. REF200-8FA286"
            value={couponCode}
            onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
            className="font-mono tracking-wider text-sm uppercase bg-white"
            data-testid="coupon-code-input"
          />
          {appliedCoupon ? (
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setAppliedCoupon(null);
                setCouponCode("");
                toast.info("Coupon removed");
              }}
              className="shrink-0 text-xs text-red-600 border-red-200"
              data-testid="remove-coupon-btn"
            >
              Remove
            </Button>
          ) : (
            <Button
              type="button"
              onClick={() => handleApplyCoupon(couponCode)}
              disabled={validatingCoupon || !couponCode.trim()}
              className="shrink-0 bg-primary hover:bg-primary/90 text-white text-xs px-4 rounded-xl"
              data-testid="apply-coupon-btn"
            >
              {validatingCoupon ? "Validating..." : "Apply Code"}
            </Button>
          )}
        </div>
      </div>

      {loadingPlans ? (
        <div className="flex justify-center items-center py-20 text-muted-foreground text-sm">
          Loading plans...
        </div>
      ) : plans.length === 0 ? (
        <div className="flex justify-center items-center py-20 text-muted-foreground text-sm">
          No subscription plans available at the moment.
        </div>
      ) : (
        <div className={`grid gap-5 mt-10 ${plans.length === 1 ? "max-w-md mx-auto grid-cols-1" : plans.length === 2 ? "max-w-2xl mx-auto grid-cols-1 md:grid-cols-2" : "grid-cols-1 md:grid-cols-3"}`}>
          {plans.map((p) => {
            const hasDiscount = appliedCoupon && p.price > 0;
            const finalPrice = hasDiscount ? Math.max(0, p.price - appliedCoupon.discount_amount) : p.price;

            return (
              <div key={p.id} className={`card-soft p-6 sm:p-8 flex flex-col ${p.featured ? "ring-2 ring-secondary scale-[1.02]" : ""}`} data-testid={`plan-${p.id}`}>
                {p.tag && <div className="inline-flex items-center gap-1 self-start text-[10px] uppercase tracking-[0.2em] text-secondary mb-2"><Sparkles className="w-3 h-3"/>{p.tag}</div>}
                <div className="font-heading text-2xl text-primary">{p.name}</div>
                <div className="mt-3 flex items-baseline gap-2">
                  {hasDiscount ? (
                    <>
                      <span className="line-through text-muted-foreground text-2xl font-bold">₹{p.price.toLocaleString("en-IN")}</span>
                      <span className="font-heading text-5xl text-emerald-600 font-bold">₹{finalPrice.toLocaleString("en-IN")}</span>
                    </>
                  ) : (
                    <span className="font-heading text-5xl text-foreground">₹{p.price.toLocaleString("en-IN")}</span>
                  )}
                  <span className="text-sm text-muted-foreground">/ {p.duration_days >= 365 ? "year" : p.duration_days >= 60 ? `${Math.round(p.duration_days / 30)} months` : `${p.duration_days} days`}</span>
                </div>

                {hasDiscount && (
                  <div className="mt-1 text-xs text-emerald-700 font-semibold flex items-center gap-1">
                    <CheckCircle2 className="w-3.5 h-3.5" /> Includes ₹200 Referral Discount!
                  </div>
                )}

                {p.description && <p className="text-sm text-muted-foreground mt-2">{p.description}</p>}
                <ul className="mt-5 space-y-2 flex-1">
                  {(p.features || []).map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm">
                      <Check className="w-4 h-4 text-secondary mt-0.5 shrink-0" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                <Button
                  onClick={() => pay(p.id, p.price)}
                  disabled={user?.premium_plan === p.id}
                  className={`mt-6 rounded-full ${
                    p.featured ? "bg-secondary hover:bg-secondary/90 text-secondary-foreground" :
                    p.price === 0 ? "bg-primary/10 hover:bg-primary/20 text-primary border border-primary/20" : ""
                  }`}
                  data-testid={`pay-${p.id}`}
                >
                  {user?.premium_plan === p.id
                    ? (p.price === 0 ? "✅ Current Plan" : "✅ Active — Renew")
                    : p.price === 0
                    ? "Activate Free Plan"
                    : hasDiscount
                    ? `Subscribe for ₹${finalPrice}`
                    : "Subscribe"}
                </Button>
              </div>
            );
          })}
        </div>
      )}

      {plans.some(p => p.price > 0) && (
        <p className="text-center text-xs text-muted-foreground mt-8">Secured by Razorpay · UPI, Cards, Netbanking &amp; Wallets supported.</p>
      )}
    </div>
  );
}
